import { useState, useEffect, useCallback } from "react";

import AddToBatchModal from "containers/admin/clients/client/approval-queue/components/add-to-batch-modal/add-to-batch-modal";

import { GeneralCommentsFAB } from "components/partials/general-comments-fab/general-comments-fab";
import { TouchpointPreviewTabContent } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-content/preview-tab-content";
import { TouchpointPreviewTabHeader } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/preview-tab-header";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";
import toast from "components/partials/toast/toast";

import {
  useTouchpointVersion,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useFeatureFlag } from "contexts/feature-flag-context";
import { useAddTouchpointToBatch } from "hooks/api/use-batches";
import { useLatestBatch } from "hooks/api/use-latest-batch";

interface PreviewTabProps {
  isSummaryLoading?: boolean;
  isVersionHistoryLoading?: boolean;
  selectedVersion: TouchpointVersionAttributes | undefined;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}

const PreviewTab = ({
  selectedVersion,
  setSelectedVersion,
  isVersionHistoryLoading,
}: PreviewTabProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const isEmailWriterGenerating = useIsEmailWriterGenerating();

  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const addTouchpointToBatchMutation = useAddTouchpointToBatch({ clientId: currentClient.id });

  const [isAddToBatchModalOpen, setIsAddToBatchModalOpen] = useState(false);

  const versionBatchId = selectedVersion?.touchpointBatchId;
  const selectedVersionId = selectedVersion?.id;
  const selectedVersionTouchpointId = selectedVersion?.touchpointId;

  const {
    data: latestBatch,
    isLoading: isLatestBatchLoading,
    isFetching: isLatestBatchFetching,
  } = useLatestBatch({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
    enabled: isAddToBatchModalOpen && isBatchManagementEnabled && !versionBatchId,
  });

  const isLatestBatchLoadingState =
    isAddToBatchModalOpen && !versionBatchId && (isLatestBatchLoading || isLatestBatchFetching);
  const latestBatchId =
    isAddToBatchModalOpen && !versionBatchId && !isLatestBatchLoadingState
      ? (latestBatch?.id ?? undefined)
      : undefined;

  const handleCloseAddToBatchModal = useCallback(() => {
    setIsAddToBatchModalOpen(false);
  }, []);

  const { data: versionDetail } = useTouchpointVersion(
    {
      clientId: currentClient.id,
      campaignId: currentCampaign.id,
      touchpointId: currentTouchpoint.id,
      id: selectedVersionId,
    },
    { enabled: selectedVersionTouchpointId === currentTouchpoint.id },
  );

  // The header, tabs and version table still read detail fields (`data`, `finalHtml`) off the parent's
  // `selectedVersion`, so the fetched detail has to be mirrored back into it. This effect is the last
  // writer: a `setSelectedVersion({ ...version, x })` elsewhere survives only because the mutation behind
  // it writes `x` into the detail cache as well.
  useEffect(() => {
    if (versionDetail && versionDetail !== selectedVersion) {
      setSelectedVersion(versionDetail);
    }
  }, [versionDetail, selectedVersion, setSelectedVersion]);

  const isVersionInBatch = !!versionBatchId;
  const batchModalTitle = isVersionInBatch ? "Change batch" : "Add to batch";
  const batchModalSubmitLabel = isVersionInBatch ? "Change batch" : "Add to batch";

  const handleAddingTouchpointToBatch = async (
    values: { batchId: string },
    selectedBatchName?: string,
  ) => {
    await addTouchpointToBatchMutation.mutateAsync({
      batchId: values.batchId,
      attributes: {
        touchpointVersionId: selectedVersion?.id ?? "",
      },
    });

    const trimmedBatchName = (selectedBatchName ?? "").trim();
    toast.success({
      data: {
        title: isVersionInBatch
          ? trimmedBatchName
            ? `Changed batch to '${trimmedBatchName}'`
            : "Changed batch"
          : trimmedBatchName
            ? `Added creative to '${trimmedBatchName}'`
            : "Added creative to batch",
      },
    });
    handleCloseAddToBatchModal();
  };

  return (
    <>
      {selectedVersion?.id && !isEmailWriterGenerating && (
        <TouchpointPreviewTabHeader
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          setIsAddToBatchModalOpen={setIsAddToBatchModalOpen}
        />
      )}
      <TouchpointPreviewTabContent
        selectedVersion={selectedVersion}
        setSelectedVersion={setSelectedVersion}
        isVersionHistoryLoading={isVersionHistoryLoading}
      />

      <GeneralCommentsFAB />

      {isAddToBatchModalOpen && (
        <AddToBatchModal
          onCloseCallback={handleCloseAddToBatchModal}
          onConfirmCallback={handleAddingTouchpointToBatch}
          versionBatchId={versionBatchId}
          latestBatchId={latestBatchId}
          isLatestBatchLoading={isLatestBatchLoadingState}
          isSubmitting={addTouchpointToBatchMutation.isPending}
          title={batchModalTitle}
          submitButtonLabel={batchModalSubmitLabel}
          submittingLabel={isVersionInBatch ? "Changing batch..." : "Adding to batch..."}
        />
      )}
    </>
  );
};

export default PreviewTab;
